/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useProfileForm.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing AI profile form state and validation.
────────────────────────────────────────────────────────────────*/

import { useState, useMemo, useCallback } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import type { AiProfile } from '../api/aiProfilesApi';
import type { AiProvider } from '../types';

type FormData = Partial<AiProfile> & Pick<AiProfile, 'domain_id' | 'type' | 'model' | 'status'>;

export function useProfileForm(
  formData: FormData,
  baseline: FormData | null,
  mode: 'create' | 'edit',
  setFormData: (data: FormData | ((prev: FormData) => FormData)) => void,
  selectedProvider?: AiProvider | null
) {
  const { t } = useI18n();
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Helper to get only fields that should be compared for changes
  const getComparableFields = useCallback((data: FormData) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const normalize = (val: any) => (val === undefined || val === '' ? null : val);

    // Normalize voice_config: it can be a string or an object with various structures
    let voiceConfig = data.voice_config;
    if (typeof voiceConfig === 'string' && voiceConfig.trim()) {
      try {
        voiceConfig = JSON.parse(voiceConfig);
      } catch (e) {
        // Keep as string if not valid JSON
      }
    }

    // Normalize voice_config to a consistent structure for comparison
    // This handles both flat (rate, pitch, volume) and nested (audioConfig.rate) structures
    let normalizedVoiceConfig: string | null = null;
    if (voiceConfig && typeof voiceConfig === 'object') {
      const vc = voiceConfig as any;
      const normalized = {
        language: vc.language || '',
        locale: vc.locale || '',
        voice: vc.voice || vc.name || '',
        voiceShortName: vc.voiceShortName || vc.name || '',
        gender: vc.gender || '',
        style: vc.style || vc.styles?.style || '',
        // Handle both flat and nested audio config
        pitch: vc.audioConfig?.pitch || vc.pitch || '0%',
        volume: vc.audioConfig?.volume || vc.volume || '100%',
        rate: vc.audioConfig?.rate || vc.rate || '1x',
      };
      normalizedVoiceConfig = JSON.stringify(normalized, Object.keys(normalized).sort());
    }

    return {
      provider_id: normalize(data.provider_id),
      type: normalize(data.type),
      model: normalize(data.model),
      temperature: normalize(data.temperature),
      max_tokens: normalize(data.max_tokens),
      voice_model: normalize(data.voice_model),
      voice_config: normalizedVoiceConfig,
      embeddings_model: normalize(data.embeddings_model),
      profile_name: normalize(data.profile_name),
      timeout_ms: normalize(data.timeout_ms),
      retry_attempts: normalize(data.retry_attempts),
      status: !!data.status,
      system_prompt: normalize(data.system_prompt),
    };
  }, []);

  // Always allow saving - change detection was causing too many edge case bugs
  const hasChanges = true;

  // Validate form
  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.profile_name || !formData.profile_name.trim()) {
      newErrors.profile_name = t('aiProfiles.validation.profileNameRequired');
    }

    if (!formData.provider_id) {
      newErrors.provider_id = t('aiProfiles.validation.providerRequired');
    }

    // Only require type for non-Azure providers
    if (selectedProvider?.provider !== 'azure' && !formData.type?.trim()) {
      newErrors.type = t('aiProfiles.validation.typeRequired');
    }

    // Only require model for non-Azure providers and non-TTS types
    if (selectedProvider?.provider !== 'azure' &&
      formData.type !== 'tts' &&
      !formData.model?.trim()) {
      newErrors.model = t('aiProfiles.validation.modelRequired');
    }

    if (formData.temperature !== undefined && (formData.temperature < 0 || formData.temperature > 1)) {
      newErrors.temperature = t('aiProfiles.validation.temperatureRange');
    }

    if (formData.max_tokens !== undefined && (formData.max_tokens < 100 || formData.max_tokens > 8000)) {
      newErrors.max_tokens = t('aiProfiles.validation.maxTokensRange');
    }

    if (formData.timeout_ms !== undefined && (formData.timeout_ms < 1000 || formData.timeout_ms > 60000)) {
      newErrors.timeout_ms = t('aiProfiles.validation.timeoutRange');
    }

    if (formData.retry_attempts !== undefined && (formData.retry_attempts < 0 || formData.retry_attempts > 5)) {
      newErrors.retry_attempts = t('aiProfiles.validation.retryAttemptsRange');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, selectedProvider, t]);

  // Handle input change
  const handleInputChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (field: string, value: any) => {
      setFormData(prev => ({ ...prev, [field]: value }));

      // Clear error when user starts typing
      if (errors[field as string]) {
        setErrors(prevErrors => {
          const draft = { ...prevErrors };
          delete draft[field as string];
          return draft;
        });
      }
    },
    [errors, setFormData]
  );

  // Get input classes helper
  const inputClasses = useCallback(
    (hasError?: boolean) =>
      `w-full px-3 py-2 text-sm border rounded bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent ${hasError ? 'border-red-500' : 'border-border'
      }`,
    []
  );

  return {
    errors,
    hasChanges,
    validateForm,
    handleInputChange,
    inputClasses
  };
}

